import { ColDef, ColGroupDef } from 'ag-grid-community';
import { reduce, flowRight as compose, property, map as mp } from 'lodash-es';
import { dateAsMoment } from '@mummssoftware/utils/dates';
import { TaskWeekCellRenderer } from './CellRenderer';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const peek = (collection: any[]) =>
  collection.length && collection[collection.length - 1];

// groups neighboring elements with value for specified property
const coalesceByProperty = (propName: string) => (collection: any[]): [any[]] =>
  reduce(
    collection,
    (seed, curr) => {
      const prev = peek(seed);
      if (prev && curr[propName] === prev[0][propName]) {
        prev.push(curr);
      } else {
        seed.push([curr]);
      }
      return seed;
    },
    [] as any,
  );

const cMap = (func: any) => (collection: any[]) => mp(collection, func);
// level 2 map: (apply to individual elements in list of lists)
const map2 = (func: any) => cMap((el: any) => mp(el, (el2: any) => func(el2)));

// groups neighboring elements by property and retains only that property in mew object
export const coalesceByPropertyAndPick = (propName: string) =>
  compose(map2(property(propName)), coalesceByProperty(propName));

export const taskToWeekMap = (task: mumms.WeekSummaryTaskDto) =>
  reduce(
    task.weeks,
    (weekMap, curr) => {
      weekMap[curr.weekstart] = { ...task, ...curr };
      return weekMap;
    },
    {} as mumms.WeekSummaryTaskRow['weeks'],
  );

export const withWeekMap = (
  task: mumms.WeekSummaryTaskDto,
): mumms.WeekSummaryTaskRow => ({
  ...task,
  weeks: taskToWeekMap(task),
});

export const asSet = (list: any[]) => new Set(list);

export const filterDuplicates = (list: any[]) => Array.from(asSet(list).keys());

export const extractStartDatesFromTaskWeekMaps = (
  tasks: mumms.WeekSummaryTaskRow[],
) =>
  reduce(
    tasks,
    (seed: string[], curr) => [...seed, ...Object.keys(curr.weeks)],
    [] as string[],
  );

export const startDatesFromTasks = compose(
  filterDuplicates,
  extractStartDatesFromTaskWeekMaps,
);

type WeekColDef = ColDef & {
  day: string | number;
  monthName: string;
  year: string;
  key: string;
};

const asColumnGroup = (headerName: string) => (
  children: any[],
): ColGroupDef => ({
  headerName,
  children,
});

const monthListToColumnGroup = (monthGroup: WeekColDef[]) =>
  asColumnGroup(monthGroup[0].monthName)(monthGroup);

const yearListToColumnGroup = (monthColGroups: ColGroupDef[]): ColGroupDef => {
  const firstMonthGroup = monthColGroups[0].children as any;
  return asColumnGroup(firstMonthGroup[0].year)(monthColGroups);
};

const groupWeekColumns = (weekColumns: WeekColDef[]): ColGroupDef[] => {
  // [[Jan 3 `25, Jan 4 `25, Nov 5 `25], [Dec 9 `26]]
  const weeksByYear = coalesceByProperty('year')(weekColumns);

  // [ [ [Jan 3 `25, Jan 4 `25], [Nov `25] ], [ [Dec `26] ] ]
  const weeksByYearMonth: WeekColDef[][][] = mp(
    weeksByYear,
    coalesceByProperty('monthName'),
  );

  // [ [ January Group, November Group ], [ December Group ] ]
  const withMonthListsAsColumnGroups: any = map2(monthListToColumnGroup)(
    weeksByYearMonth,
  );

  // [ 2025 Group, 2026 Group ]
  const withYearListsAsColumnGroups: any = mp(
    withMonthListsAsColumnGroups,
    yearListToColumnGroup,
  );
  return withYearListsAsColumnGroups;
};

const weekStartDateToColumnDef = (startDate: string): WeekColDef => {
  const [year, month, day] = startDate.split('-');
  const monthName = months[Number.parseInt(month) - 1].toUpperCase();
  return {
    day,
    monthName,
    year,
    headerName: day,
    width: 40,
    cellClass: ['width100', 'textAlignCenter', 'borderRight'],
    headerClass: ['width100', 'textAlignCenter'],
    suppressMenu: true,
    cellRendererFramework: TaskWeekCellRenderer,
    cellRendererParams: { startDate },
    key: startDate,
    colId: startDate,
  };
};

export const weekStartDatesToGroupedColumns = (startDates: string[]) => {
  const weekColumns = mp(startDates, weekStartDateToColumnDef);
  return groupWeekColumns(weekColumns);
};

const daysOfWeekISO: string[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const daysFromWeekdayToWeekday = (fromIndex: number, toIndex: number) =>
  (toIndex - fromIndex + daysOfWeekISO.length) % daysOfWeekISO.length;

const addDays = (n: number) => dateAsMoment((m) => m.add(n, 'day'));

const subtractDays = (n: number) => dateAsMoment((m) => m.subtract(n, 'day'));

// i.e. firstSUNdayBeforeDate
const firstXDayBeforeDate = (dayOfWeek: string, date: Date) => {
  const targetWeekdayIndex = daysOfWeekISO.indexOf(dayOfWeek);
  const dateWeekdayIndex = date.getDay(); // Sunday indexed to 0
  const daysBack = daysFromWeekdayToWeekday(
    targetWeekdayIndex,
    dateWeekdayIndex,
  );
  return subtractDays(daysBack)(date);
};

// Returns all week-start dates of the given weekday whose week overlaps [start, end].
// The first column is the week CONTAINING start, so the user's selected date is always visible.
export const allXdaysInRange = (dayOfWeek: string, start: Date, end: Date) => {
  const firstXDay = firstXDayBeforeDate(dayOfWeek, start);

  const dateList = [] as Date[];
  let iDate = firstXDay;
  while (iDate <= end) {
    dateList.push(iDate);
    iDate = addDays(7)(iDate);
  }
  return dateList;
};
